import CardState, { type CardStateValue } from '@/components/ui/CardState'
import Change from '@/components/ui/Change'
import Money from '@/components/ui/Money'
import SectionHead from '@/components/ui/SectionHead'
import SeeAllLink from '@/components/ui/SeeAllLink'
import Skeleton from '@/components/ui/Skeleton'
import SymbolLink from '@/components/ui/SymbolLink'
import { useRailMovers } from '@/features/market-rail/api/queries'
import type { RailMovers } from '@/features/market-rail/api/railSchemas'
import RailRows from '@/features/market-rail/components/RailRows'
import { useRailBlock } from '@/features/market-rail/useRailFit'

/** One row's box at `py-2` around a 20px line, 36px — the row below. */
function MoverRowSkeleton() {
  return (
    <div className="flex items-center justify-between gap-2 py-2">
      <Skeleton className="h-5 w-16 rounded-xs" />
      <div className="flex shrink-0 items-center gap-2">
        <Skeleton className="h-5 w-14 rounded-xs" />
        <Skeleton className="h-5 w-12 rounded-xs" />
      </div>
    </div>
  )
}

const SKELETON_ROWS = Array.from({ length: 4 }, (_, index) => <MoverRowSkeleton key={index} />)

/**
 * One Top movers row (the mock's `mrail__mover`): ticker, price and move at 14, a hairline above
 * every row but the first. At rest the ticker and price sit in the secondary grey; the row's hover
 * (the rail's wash) lifts them to the foreground. The ticker's link stretches over the row, so the
 * row is one target named by the ticker, and it opens the stock.
 */
function MoverRow({ row }: { row: RailMovers['gainers'][number] }) {
  return (
    <div className="relative flex items-center gap-2 border-t border-border-subtle py-2 text-body-m text-muted-foreground rail-row-wash first:border-t-0 hover:text-foreground focus-within:text-foreground">
      <SymbolLink
        symbol={row.symbol}
        className="flex-1 truncate font-semibold after:absolute after:inset-0 after:content-['']"
      />
      {/* No "Rs": the column is all one currency. */}
      <Money value={row.ltp} className="w-21 shrink-0 text-right" />
      <Change value={row.percentChange} percent className="w-18 shrink-0 justify-end" />
    </div>
  )
}

/**
 * "Top movers" on the Market rail (the mock's): the day's two biggest gainers over its two biggest
 * fallers, with "All movers" to the full board. On `/market` it reads the same movers entries the
 * page's own pair does, so it costs no request there. The rail's fit trims rows from the bottom on
 * a short window, and drops the block before Market news.
 */
export default function TopMoversCard() {
  const block = useRailBlock('movers')
  const movers = useRailMovers()
  const { data, isPending, isError } = movers
  const rows = data === undefined ? [] : [...data.gainers, ...data.losers]

  const state: CardStateValue = isPending
    ? 'loading'
    : isError
      ? 'failed'
      : rows.length === 0
        ? 'empty'
        : 'loaded'

  return (
    <section aria-label="Top movers" {...block}>
      <SectionHead
        size="sm"
        title="Top movers"
        action={<SeeAllLink to="/market/movers">All movers</SeeAllLink>}
      />
      <div className="mt-1">
        <CardState
          state={state}
          skeleton={<RailRows block="movers" rows={SKELETON_ROWS} />}
          onRetry={() => {
            void movers.refetch()
          }}
          retrying={movers.isFetching}
          failedTitle="Couldn't load the movers"
          failedDescription="The movers feed is not answering. Try again."
          emptyTitle="No movers yet"
          emptyDescription="The list fills once the session trades."
        >
          <RailRows
            block="movers"
            rows={rows.map((row, index) => (
              // Gainers first, then losers: the place keys the row, as a thin day can put one
              // name on both lists.
              <MoverRow key={`${String(index)}-${row.symbol}`} row={row} />
            ))}
          />
        </CardState>
      </div>
    </section>
  )
}
